import axios from '../../../lib/utils/axios_utils.js?vue3';
import { compatH } from '../../../lib/utils/vue3compat/compat_h.js?vue3';
import { initVueApp } from '../../../lib/utils/vue3compat/init_vue_app.js?vue3';
import { normalizeRender } from '../../../lib/utils/vue3compat/normalize_render.js?vue3';
import { pinia } from '../../../pinia/instance.js?vue3';
import { DiffFile } from '~/rapid_diffs/web_components/diff_file';
import FileBrowserToggle from '../../../diffs/components/file_browser_toggle.vue?vue3';
import { useMainContainer } from '../../../pinia/global_stores/main_container.js?vue3';
import { keyboardShortcutsDisabled } from '~/behaviors/shortcuts/shortcuts_disabled';
import { keysFor, MR_FOCUS_FILE_BROWSER } from '~/behaviors/shortcuts/keybindings';
import { Mousetrap } from '~/lib/mousetrap';
import { useApp } from '../../stores/app.js?vue3';
import { useFileBrowser } from '../../../diffs/stores/file_browser.js?vue3';
import { useDiffsList } from '../../stores/diffs_list.js?vue3';
import { useDiffsView } from '../../stores/diffs_view.js?vue3';
import { withLinkedFileUrlParams } from '~/rapid_diffs/utils/linked_file';
import FileBrowser from './file_browser.vue?vue3';
import FileBrowserDrawer from './file_browser_drawer.vue?vue3';
import FileBrowserDrawerToggle from './file_browser_drawer_toggle.vue?vue3';

function addFileLinks(diffFiles) {
  return diffFiles.map((diffFile) => {
    return Object.assign(diffFile, {
      href: withLinkedFileUrlParams(new URL(window.location), {
        oldPath: diffFile.old_path,
        newPath: diffFile.new_path,
        hash: diffFile.file_hash,
      }).toString(),
    });
  });
}

const loadFileBrowserData = async (diffFilesEndpoint, shouldSort) => {
  const { data } = await axios.get(diffFilesEndpoint);
  useFileBrowser().setTreeData(addFileLinks(data.diff_files), shouldSort);
};

const bindFocusShortcut = () => {
  const focusFileBrowser = () => {
    const store = useFileBrowser();

    if (useMainContainer().isCompact) {
      store.setFileBrowserDrawerVisibility(true);
    } else {
      store.setFileBrowserVisibility(true);
    }

    store.requestSearchFocus();
  };

  Mousetrap.bind(keysFor(MR_FOCUS_FILE_BROWSER), focusFileBrowser);
};

const initToggle = (el) => {
  initVueApp({
    el: document.querySelector('#js-page-breadcrumbs-extra'),
    name: 'FileBrowserDrawerToggleRoot',
    pinia,
    component: normalizeRender({
      name: 'FileBrowserDrawerToggleApp',
      computed: {
        visible() {
          return useMainContainer().isCompact && useApp().appVisible;
        },
      },
      render() {
        if (!this.visible) return null;

        return compatH(FileBrowserDrawerToggle);
      },
    }),
  });

  initVueApp({
    el,
    name: 'FileBrowserToggleRoot',
    pinia,
    component: normalizeRender({
      name: 'FileBrowserToggleApp',
      computed: {
        visible() {
          return !useMainContainer().isCompact;
        },
      },
      render() {
        if (!this.visible) return null;

        return compatH(FileBrowserToggle, { props: { bindFocusShortcut: false } });
      },
    }),
  });
};

const initBrowserComponent = async (el, shouldSort, provide) => {
  initVueApp({
    el,
    name: 'FileBrowserRoot',
    pinia,
    provide,
    component: normalizeRender({
      name: 'FileBrowserApp',
      render() {
        return compatH(useMainContainer().isCompact ? FileBrowserDrawer : FileBrowser, {
          props: {
            groupBlobsListItems: shouldSort,
            linkedFilePath: useDiffsList().linkedFilePath,
          },
          on: {
            'click-file': (file) => {
              const diffsView = useDiffsView(pinia);

              if (diffsView.singleFileMode) {
                const index = useFileBrowser(pinia).flatBlobsList.findIndex(
                  (entry) => entry.fileHash === file.fileHash,
                );

                if (index >= 0) diffsView.goToFile(index);
                return;
              }

              DiffFile.findByFileHash(file.fileHash).selectFile();
            },
          },
        });
      },
    }),
  });
};

export async function initFileBrowser({ toggleTarget, browserTarget, appData, provide = {} }) {
  initToggle(toggleTarget);
  if (!keyboardShortcutsDisabled()) bindFocusShortcut();
  useFileBrowser().initTreeList();
  await loadFileBrowserData(appData.diffFilesEndpoint, appData.shouldSortMetadataFiles);
  useFileBrowser().initFileBrowserVisibility({ autoHide: true });
  initBrowserComponent(browserTarget, appData.shouldSortMetadataFiles, provide);
}
